import { useState } from 'react';
import { format } from 'date-fns';
import { Pencil, Trash2, MoreVertical, FileText } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { CalculationItemProps } from '../types';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { useToast } from '@/hooks/use-toast';

export function CalculationItem({
  calculation,
  onLoad,
  onDelete,
  onRename,
  isActive,
  isMinimized = false,
}: CalculationItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(calculation.project_name);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const { toast } = useToast();

  const handleRename = async () => {
    if (newName.trim() && newName !== calculation.project_name) {
      try {
        setIsRenaming(true);
        await onRename(calculation.id, newName.trim());
        toast({ title: 'Success', description: 'Calculation renamed' });
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to rename',
          variant: 'destructive',
        });
        setNewName(calculation.project_name);
      } finally {
        setIsRenaming(false);
      }
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRename();
    }
    if (e.key === 'Escape') {
      setNewName(calculation.project_name);
      setIsEditing(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(calculation.id);
      toast({ title: 'Success', description: 'Calculation deleted' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isMinimized) {
    return (
      <div
        className={cn(
          "group flex items-center justify-center p-2 hover:bg-muted/50 rounded-lg transition-colors",
          isActive && "bg-muted"
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onLoad(calculation)}
        >
          <FileText className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "group flex items-center gap-2 px-3 py-2 rounded-lg",
          "border-l-2 border-transparent", // Reserve space to prevent layout shift
          "motion-safe:transition-all motion-safe:duration-150",
          "hover:bg-muted/60 hover:shadow-sm",
          isActive && "bg-primary/10 border-l-primary" // Just change border color, not add border
        )}
      >
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={handleRename}
              onKeyDown={handleKeyDown}
              className="h-7"
              autoFocus
              disabled={isRenaming}
            />
          ) : (
            <>
              <div className="font-medium truncate">{calculation.project_name}</div>
              <div className="text-xs text-muted-foreground">
                {format(new Date(calculation.date_saved), 'PPp')}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onLoad(calculation)}
          >
            <FileText className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
        projectName={calculation.project_name}
        isDeleting={isDeleting}
      />
    </>
  );
}